import React, { useState } from 'react';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { marketingImages } from './MarketingPrimitives';
import {
  marketingColor,
  marketingFont,
  marketingLayout,
  marketingLine,
  marketingRadius,
} from '../../theme/marketingTokens';

const hairlineGridSx = {
  display: 'grid',
  gap: '1px',
  backgroundColor: marketingLine.hairline,
  border: `1px solid ${marketingLine.hairline}`,
} as const;

/**
 * Mission, the Mozi naming story, the single supported stat, and the
 * principles rail.
 *
 * No figures here. The product, user and API-call counts this page used to
 * show were removed as unsupported, and the uptime figure went with them —
 * nothing in this system measures sync uptime, and /health is a liveness
 * probe, not an SLA. Don't re-add that kind of claim without real numbers.
 */
export const AboutMission: React.FC = () => {
  const { t } = useTranslation() as { t: (key: string, options?: any) => any };
  const principles = t('public.about.whyMotzu.principles', {
    returnObjects: true,
  }) as string[];

  return (
    <Box
      component="section"
      sx={{
        backgroundColor: marketingColor.surface,
        borderBottom: `1px solid ${marketingLine.hairline}`,
        px: marketingLayout.sectionPaddingX,
        py: { xs: 7, md: 10.5 },
      }}
    >
      <Box
        sx={{
          maxWidth: marketingLayout.maxWidth,
          mx: 'auto',
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1.1fr 1fr' },
          gap: { xs: 5, md: 6.5 },
          alignItems: 'center',
        }}
      >
        <Box>
          <Typography variant="h3" sx={{ fontSize: { xs: '1.65rem', md: '1.875rem' }, mb: 2 }}>
            {t('public.about.mission.title')}
          </Typography>
          <Typography
            sx={{ fontSize: 14.5, color: marketingColor.textSecondary, lineHeight: 1.65, mb: 1.75 }}
          >
            {t('public.about.mission.subtitle')}
          </Typography>
          <Typography
            sx={{ fontSize: 14.5, color: marketingColor.textSecondary, lineHeight: 1.65, mb: 2.5 }}
          >
            {t('public.about.whyMotzu.subtitle')}
          </Typography>

        </Box>

        <Box sx={{ ...hairlineGridSx, gridTemplateColumns: '1fr' }}>
          {(Array.isArray(principles) ? principles : []).map((principle) => (
            <Box
              key={principle}
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1.25,
                backgroundColor: marketingColor.ink,
                px: 2.25,
                py: 2,
              }}
            >
              <Box
                aria-hidden
                sx={{
                  fontFamily: marketingFont.mono,
                  fontWeight: 700,
                  fontSize: 13,
                  color: marketingColor.accent,
                  flexShrink: 0,
                }}
              >
                &gt;
              </Box>
              <Box sx={{ fontSize: 13.5, color: marketingColor.text }}>{principle}</Box>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export const AboutBuilding: React.FC = () => {
  const { t } = useTranslation() as { t: (key: string, options?: any) => any };
  const cards = t('public.about.whatBuilding.cards', { returnObjects: true }) as {
    title: string;
    description: string;
  }[];

  return (
    <Box
      component="section"
      sx={{
        maxWidth: marketingLayout.maxWidth,
        mx: 'auto',
        px: marketingLayout.sectionPaddingX,
        py: { xs: 7, md: 10.5 },
      }}
    >
      <Typography
        variant="h3"
        sx={{ fontSize: { xs: '1.65rem', md: '1.875rem' }, mb: 1.5, textAlign: 'center' }}
      >
        {t('public.about.whatBuilding.title')}
      </Typography>
      <Typography
        sx={{
          fontSize: 14.5,
          color: marketingColor.textSecondary,
          textAlign: 'center',
          maxWidth: 640,
          mx: 'auto',
          mb: 5.5,
        }}
      >
        {t('public.about.whatBuilding.subtitle')}
      </Typography>
      <Box
        sx={{ ...hairlineGridSx, gridTemplateColumns: { xs: 'minmax(0,1fr)', md: 'repeat(auto-fit, minmax(260px, 1fr))' } }}
      >
        {(Array.isArray(cards) ? cards : []).map((card) => (
          <Box key={card.title} sx={{ backgroundColor: marketingColor.surface, p: 3.25 }}>
            <Typography
              sx={{
                fontFamily: marketingFont.display,
                fontSize: 15.5,
                fontWeight: 700,
                color: marketingColor.text,
                mb: 1,
              }}
            >
              {card.title}
            </Typography>
            <Typography sx={{ fontSize: 13, color: marketingColor.textSecondary, lineHeight: 1.6 }}>
              {card.description}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

/** Initials for the avatar square, e.g. "Tracey Stewart" → "TS". */
const initialsOf = (name: string) =>
  name
    .split(' ')
    .map((part) => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

/**
 * Headshot when one is supplied and loads, initials otherwise.
 *
 * The fallback is per-card state rather than a check on the path, so a file
 * that is missing, misnamed or corrupt degrades to the initials this card drew
 * before — a broken-image icon on a leadership page is worse than no photo.
 */
const TeamAvatar: React.FC<{ id: string; name: string }> = ({ id, name }) => {
  const [failed, setFailed] = useState(false);
  const src = marketingImages.team[id];
  const frameSx = {
    width: 76,
    height: 76,
    mx: 'auto',
    mb: 2.25,
    border: `1px solid ${marketingColor.accent}`,
    // Rounded square rather than a circle: `marketingRadius.lg` is the radius
    // the surrounding card uses, so the avatar echoes it instead of
    // introducing a second shape language.
    borderRadius: `${marketingRadius.lg}px`,
    overflow: 'hidden',
  } as const;

  if (src && !failed) {
    return (
      <Box sx={frameSx}>
        <Box
          component="img"
          src={src}
          alt={name}
          onError={() => setFailed(true)}
          sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
        />
      </Box>
    );
  }

  return (
    <Box
      aria-hidden
      sx={{
        ...frameSx,
        color: marketingColor.accent,
        fontFamily: marketingFont.mono,
        fontWeight: 700,
        fontSize: 22,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {initialsOf(name)}
    </Box>
  );
};

export const AboutTeam: React.FC = () => {
  const { t } = useTranslation() as { t: (key: string, options?: any) => any };
  const members = t('public.about.team.members', { returnObjects: true }) as {
    id: string;
    name: string;
    role: string;
    bio: string;
  }[];

  return (
    <Box
      component="section"
      sx={{
        backgroundColor: marketingColor.surface,
        borderTop: `1px solid ${marketingLine.hairline}`,
        borderBottom: `1px solid ${marketingLine.hairline}`,
        px: marketingLayout.sectionPaddingX,
        py: { xs: 7, md: 10.5 },
      }}
    >
      <Box sx={{ maxWidth: marketingLayout.maxWidth, mx: 'auto' }}>
        <Typography
          variant="h3"
          sx={{ fontSize: { xs: '1.65rem', md: '1.875rem' }, mb: 5.5, textAlign: 'center' }}
        >
          {t('public.about.team.title')}
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: 'minmax(0,1fr)', md: 'repeat(auto-fit, minmax(240px, 1fr))' },
            gap: 3,
          }}
        >
          {(Array.isArray(members) ? members : []).map((member) => (
            <Box
              key={member.name}
              sx={{
                textAlign: 'center',
                px: 2.25,
                py: 3.5,
                border: '1px solid rgba(95,226,138,0.12)',
                borderRadius: '8px',
                transition: 'border-color 0.25s ease, transform 0.25s ease, background-color 0.25s ease',
                '&:hover': {
                  borderColor: marketingLine.hairlineHover,
                  transform: 'translateY(-6px)',
                  backgroundColor: marketingColor.ink,
                },
                '@media (prefers-reduced-motion: reduce)': {
                  '&:hover': { transform: 'none' },
                },
              }}
            >
              <TeamAvatar id={member.id} name={member.name} />
              <Typography
                sx={{
                  fontFamily: marketingFont.display,
                  fontSize: 17,
                  fontWeight: 700,
                  color: marketingColor.text,
                  mb: 0.5,
                }}
              >
                {member.name}
              </Typography>
              <Typography
                sx={{
                  fontFamily: marketingFont.mono,
                  fontSize: 11,
                  color: marketingColor.accent,
                  textTransform: 'uppercase',
                  mb: 1.5,
                }}
              >
                {member.role}
              </Typography>
              <Typography
                sx={{
                  fontSize: 13,
                  color: marketingColor.textSecondary,
                  lineHeight: 1.6,
                  maxWidth: 280,
                  mx: 'auto',
                }}
              >
                {member.bio}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

/**
 * Scroll-revealed journey timeline.
 *
 * The track fills with `scaleY(progress)` and each row reveals once progress
 * passes `(i + 0.15) / n`, so the dots light in step with the fill.
 */
export const AboutTimeline: React.FC<{
  sectionRef: React.RefObject<HTMLDivElement | null>;
  progress: number;
}> = ({ sectionRef, progress }) => {
  const { t } = useTranslation() as { t: (key: string, options?: any) => any };
  const rows = t('public.about.timeline', { returnObjects: true }) as {
    year: string;
    title: string;
    description: string;
  }[];
  const list = Array.isArray(rows) ? rows : [];

  return (
    <Box
      component="section"
      ref={sectionRef}
      sx={{
        maxWidth: 1000,
        mx: 'auto',
        px: marketingLayout.sectionPaddingX,
        py: { xs: 7, md: 10.5 },
      }}
    >
      <Typography
        variant="h3"
        sx={{ fontSize: { xs: '1.65rem', md: '1.875rem' }, mb: 5.5, textAlign: 'center' }}
      >
        {t('public.about.timelineSection.title')}
      </Typography>
      <Box sx={{ position: 'relative', pl: 3.25 }}>
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            top: 6,
            bottom: 6,
            left: 3,
            width: '2px',
            backgroundColor: marketingLine.hairline,
          }}
        >
          <Box
            sx={{
              width: '100%',
              height: '100%',
              backgroundColor: marketingColor.accent,
              transformOrigin: 'top',
              transform: `scaleY(${progress})`,
              transition: 'transform 0.1s linear',
            }}
          />
        </Box>
        {list.map((row, index) => {
          const revealed = progress > (index + 0.15) / list.length;
          return (
            <Box
              key={row.year}
              sx={{
                position: 'relative',
                display: 'flex',
                gap: { xs: 2, md: 3.25 },
                flexWrap: { xs: 'wrap', sm: 'nowrap' },
                py: 2.5,
                borderBottom: `1px solid ${marketingLine.hairline}`,
                opacity: revealed ? 1 : 0,
                transform: revealed ? 'translateX(0)' : 'translateX(-16px)',
                transition: 'opacity 0.6s ease, transform 0.6s ease',
              }}
            >
              <Box
                aria-hidden
                sx={{
                  position: 'absolute',
                  left: -30,
                  top: 26,
                  width: 8,
                  height: 8,
                  borderRadius: '999px',
                  backgroundColor: revealed
                    ? marketingColor.accent
                    : marketingColor.surfaceStrong,
                }}
              />
              <Box
                sx={{
                  // 90px in the handoff, but its own longest value
                  // ("Sept 2026") wraps at that width.
                  width: 110,
                  flexShrink: 0,
                  whiteSpace: 'nowrap',
                  fontFamily: marketingFont.mono,
                  fontWeight: 700,
                  fontSize: 14,
                  color: marketingColor.accent,
                }}
              >
                [{row.year}]
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  sx={{
                    fontFamily: marketingFont.display,
                    fontSize: 15,
                    fontWeight: 700,
                    color: marketingColor.text,
                    mb: 0.5,
                  }}
                >
                  {row.title}
                </Typography>
                <Typography
                  sx={{ fontSize: 13, color: marketingColor.textSecondary, lineHeight: 1.6 }}
                >
                  {row.description}
                </Typography>
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};
